import { useEffect, useState } from "react";
import { api } from "../api/client";
import {
  EmptyState,
  ErrorBox,
  SkeletonRows,
  StatusPill,
} from "../components/States";
import { PullToRefresh } from "../components/PullToRefresh";
import { Tone } from "../theme";

interface HealthData {
  now: string;
  scheduler: {
    alive: boolean;
    heartbeat_age_sec: number | null;
    heartbeat_at: string | null;
  };
  backup: {
    ok?: boolean;
    file?: string;
    verified?: boolean;
    verify_detail?: string;
    finished_at?: string;
    uploaded?: boolean | null;
    error?: string;
  };
  rates: {
    last_fetched_at: string | null;
    age_hours: number | null;
    provider: string;
  };
  crypto: {
    wallets_total: number;
    wallets_active: number;
    last_polled_at: string | null;
    poll_age_hours: number | null;
    tronscan_key: boolean;
    txs_total: number;
  };
  db: { size_mb: number | null; path: string };
}

function fmtDT(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function Card({
  title,
  tone,
  pill,
  rows,
}: {
  title: string;
  tone: Tone;
  pill: string;
  rows: Array<[string, string]>;
}) {
  return (
    <div className="bg-tg-sectionBg rounded-xl p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">{title}</span>
        <StatusPill tone={tone}>{pill}</StatusPill>
      </div>
      <dl className="mt-2 text-xs grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-tg-hint">{k}</dt>
            <dd className="text-tg-text break-words text-right">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function HealthPage() {
  const [data, setData] = useState<HealthData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setData(await api.get<HealthData>("/api/health/dashboard"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-3">
        <h1 className="text-xl font-semibold">🩺 Состояние системы</h1>
        {error && <ErrorBox text={error} />}
        {!data && !error && <SkeletonRows rows={5} />}
        {data && (
          <>
            <Card
              title="Планировщик / процесс бота"
              tone={data.scheduler.alive ? "success" : "danger"}
              pill={data.scheduler.alive ? "активен" : "нет сигнала"}
              rows={[
                ["Последний heartbeat", fmtDT(data.scheduler.heartbeat_at)],
                [
                  "Давность",
                  data.scheduler.heartbeat_age_sec == null
                    ? "—"
                    : `${data.scheduler.heartbeat_age_sec} с`,
                ],
              ]}
            />

            <Card
              title="Резервное копирование"
              tone={backupTone(data.backup)}
              pill={backupPill(data.backup)}
              rows={[
                ["Файл", data.backup.file || "—"],
                ["Проверка", data.backup.verify_detail || "—"],
                [
                  "Выгрузка на Я.Диск",
                  data.backup.uploaded == null
                    ? "не настроена"
                    : data.backup.uploaded
                    ? "выполнена"
                    : "ошибка",
                ],
                ["Завершён", fmtDT(data.backup.finished_at)],
                ...(data.backup.error
                  ? ([["Ошибка", data.backup.error]] as Array<
                      [string, string]
                    >)
                  : []),
              ]}
            />

            <Card
              title="Курсы ЦБ"
              tone={
                data.rates.age_hours == null
                  ? "neutral"
                  : data.rates.age_hours > 36
                  ? "warning"
                  : "success"
              }
              pill={
                data.rates.age_hours == null
                  ? "нет данных"
                  : data.rates.age_hours > 36
                  ? "устарели"
                  : "свежие"
              }
              rows={[
                ["Обновлены", fmtDT(data.rates.last_fetched_at)],
                [
                  "Давность",
                  data.rates.age_hours == null
                    ? "—"
                    : `${data.rates.age_hours} ч`,
                ],
                ["Провайдер", data.rates.provider],
              ]}
            />

            <Card
              title="Крипто-мониторинг"
              tone={cryptoTone(data.crypto)}
              pill={cryptoPill(data.crypto)}
              rows={[
                [
                  "Кошельки",
                  `${data.crypto.wallets_active} активн. / ${data.crypto.wallets_total} всего`,
                ],
                ["Последний опрос", fmtDT(data.crypto.last_polled_at)],
                [
                  "Давность опроса",
                  data.crypto.poll_age_hours == null
                    ? "—"
                    : `${data.crypto.poll_age_hours} ч`,
                ],
                [
                  "Ключ TronScan",
                  data.crypto.tronscan_key ? "задан" : "не задан",
                ],
                ["Транзакций в базе", String(data.crypto.txs_total)],
              ]}
            />

            <Card
              title="База данных"
              tone="neutral"
              pill="SQLite"
              rows={[
                [
                  "Размер",
                  data.db.size_mb == null ? "—" : `${data.db.size_mb} МБ`,
                ],
                ["Путь", data.db.path],
              ]}
            />

            <div className="text-[11px] text-tg-hint text-center">
              Снято: {fmtDT(data.now)}
            </div>
          </>
        )}
        {data === null && error && (
          <EmptyState icon="🩺" text="Не удалось получить состояние." />
        )}
      </div>
    </PullToRefresh>
  );
}

function backupTone(b: HealthData["backup"]): Tone {
  if (b.ok === undefined) return "neutral";
  return b.ok ? "success" : "danger";
}
function backupPill(b: HealthData["backup"]): string {
  if (b.ok === undefined) return "ещё не запускался";
  return b.ok ? "ок" : "сбой";
}
function cryptoTone(c: HealthData["crypto"]): Tone {
  if (c.wallets_active === 0) return "neutral";
  if (c.poll_age_hours != null && c.poll_age_hours > 6) return "warning";
  return "success";
}
function cryptoPill(c: HealthData["crypto"]): string {
  if (c.wallets_active === 0) return "нет кошельков";
  if (c.poll_age_hours != null && c.poll_age_hours > 6) return "опрос отстаёт";
  return "работает";
}
